import React, { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { SocialPoll } from '@/api/entities';
import { getTrackedPrograms, addTrackedProgram, deleteTrackedProgram } from '@/lib/database/tracked-programs-helpers';
import { getEmailConnections } from '@/lib/database/email-connections-helpers';
import { Button } from '@/components/ui/button';

export default function DatabaseTest() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const addResult = (test, status, data) => {
    setResults(prev => [...prev, { test, status, data, timestamp: new Date().toISOString() }]);
  };

  const runTests = async () => {
    setLoading(true);
    setResults([]);

    try {
      // Test 1: Check Supabase connection
      addResult('Supabase Connection', 'testing', 'Checking connection...');
      const { data: connectionTest, error: connectionError } = await supabase
        .from('camp_audit_data')
        .select('count')
        .limit(1);
      
      if (connectionError) {
        addResult('Supabase Connection', 'error', connectionError.message);
      } else {
        addResult('Supabase Connection', 'success', 'Connected successfully');
      }

      // Test 2: Check authentication
      addResult('Authentication', 'testing', 'Checking user auth...');
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError) {
        addResult('Authentication', 'error', authError.message);
      } else if (!user) {
        addResult('Authentication', 'warning', 'No user logged in');
      } else {
        addResult('Authentication', 'success', `User: ${user.email || user.id}`);
      }

      // Test 3: Check Tracked Programs (migrated from Camp table)
      addResult('Tracked Programs', 'testing', 'Checking tracked programs...');
      try {
        if (user) {
          const programs = await getTrackedPrograms(user.id);
          addResult('Tracked Programs', 'success', `Found ${programs.length} tracked programs`);
        } else {
          addResult('Tracked Programs', 'warning', 'No user logged in - skipping test');
        }
      } catch (error) {
        addResult('Tracked Programs', 'error', error.message);
      }

      // Test 4: Check Email Connections (migrated functionality)
      addResult('Email Connections', 'testing', 'Checking email connections...');
      try {
        if (user) {
          const connections = await getEmailConnections(user.id);
          addResult('Email Connections', 'success', `Found ${connections.length} email connections`);
        } else {
          addResult('Email Connections', 'warning', 'No user logged in - skipping test');
        }
      } catch (error) {
        addResult('Email Connections', 'error', error.message);
      }

      // Test 5: Check SocialPoll table structure
      addResult('SocialPoll Table', 'testing', 'Checking socialpoll table...');
      try {
        const { data: pollData, error: pollError } = await supabase
          .from('social_polls')
          .select('*')
          .limit(1);
        
        if (pollError) {
          addResult('SocialPoll Table', 'error', pollError.message);
        } else {
          addResult('SocialPoll Table', 'success', `Table accessible, ${pollData.length} records found`);
        }
      } catch (error) {
        addResult('SocialPoll Table', 'error', error.message);
      }

      // Test 6: Try creating a test poll (if user is authenticated)
      if (user) {
        addResult('Create Poll Test', 'testing', 'Testing poll creation...');
        try {
          const testPollData = {
            camp_id: '00000000-0000-0000-0000-000000000000', // Dummy UUID
            question: 'Test poll - please ignore',
            status: 'active',
            votes: []
          };

          const result = await SocialPoll.create(testPollData);
          addResult('Create Poll Test', 'success', 'Poll created successfully');
          
          // Clean up - delete the test poll
          if (result?.id) {
            await supabase.from('social_polls').delete().eq('id', result.id);
            addResult('Cleanup', 'success', 'Test poll deleted');
          }
        } catch (error) {
          addResult('Create Poll Test', 'error', error.message);
        }
      }

      // Test 7: Test Tracked Programs CRUD (if user is authenticated)
      if (user) {
        addResult('Tracked Programs CRUD', 'testing', 'Testing tracked programs CRUD...');
        try {
          // Create a test program
          const testProgram = await addTrackedProgram(user.id, {
            program_type: 'test_camp',
            status: 'considering',
            year_tracked: new Date().getFullYear(),
            child_names: ['Test Child'],
            program_data: { test: true }
          });
          addResult('Tracked Programs CRUD', 'success', 'Test program created successfully');
          
          // Clean up - delete the test program
          if (testProgram?.id) {
            await deleteTrackedProgram(user.id, testProgram.id);
            addResult('Tracked Programs Cleanup', 'success', 'Test program deleted');
          }
        } catch (error) {
          addResult('Tracked Programs CRUD', 'error', error.message);
        }
      }

    } catch (error) {
      addResult('General Error', 'error', error.message);
    }

    setLoading(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'text-green-600';
      case 'error': return 'text-red-600';
      case 'warning': return 'text-yellow-600';
      case 'testing': return 'text-blue-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-4">Database Migration Test</h2>
      
      <Button 
        onClick={runTests} 
        disabled={loading}
        className="mb-6"
      >
        {loading ? 'Running Tests...' : 'Run Migration Tests'}
      </Button>

      <div className="space-y-3">
        {results.map((result, index) => (
          <div key={index} className="border rounded-lg p-4 bg-white shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold">{result.test}</h3>
              <span className={`text-sm font-medium ${getStatusColor(result.status)}`}>
                {result.status.toUpperCase()}
              </span>
            </div>
            <p className="text-sm text-gray-600">{result.data}</p>
            <p className="text-xs text-gray-400 mt-1">{new Date(result.timestamp).toLocaleTimeString()}</p>
          </div>
        ))}
      </div>

      {results.length === 0 && !loading && (
        <p className="text-gray-500 text-center py-8">
          Click "Run Migration Tests" to verify the database migration is working correctly
        </p>
      )}
    </div>
  );
}
